import { CORRELATION_ATTRIBUTES, sanitizeEventId, sanitizeSessionId } from "@clientportal/core/observability";
import { setContext, setTag } from "@sentry/react";
import posthog, { type PostHogInterface } from "posthog-js";

export interface RequestCorrelation {
  sessionId?: string;
  eventId: string;
}

export interface ObservabilityContext {
  sessionId?: string;
  eventId?: string;
  replayUrl?: string;
}

export const SERVICE_VERSION = import.meta.env.VITE_SERVICE_VERSION;
export const DEPLOYMENT_ENVIRONMENT = import.meta.env.VITE_DEPLOYMENT_ENVIRONMENT ?? import.meta.env.VITE_ENV;
export const ACTIVE_EVENT_CONTEXT_TTL_MS = 15_000;

let activeEventId: string | undefined;
let activeEventExpiresAt = 0;

const clearActiveEvent = (): void => {
  activeEventId = undefined;
  activeEventExpiresAt = 0;
  setTag(CORRELATION_ATTRIBUTES.eventId, undefined);
};

const getActiveEventId = (): string | undefined => {
  if (activeEventId && Date.now() >= activeEventExpiresAt) clearActiveEvent();
  return activeEventId;
};

const getPostHogSessionId = (client: PostHogInterface = posthog): string | undefined => {
  try {
    return sanitizeSessionId(client.get_session_id());
  } catch {
    return undefined;
  }
};

const getPostHogReplayUrl = (client: PostHogInterface = posthog): string | undefined => {
  try {
    return client.get_session_replay_url() || undefined;
  } catch {
    return undefined;
  }
};

export const createRequestCorrelation = (client: PostHogInterface = posthog): RequestCorrelation => ({
  sessionId: getPostHogSessionId(client),
  eventId: crypto.randomUUID(),
});

export const getObservabilityContext = (client: PostHogInterface = posthog): ObservabilityContext => ({
  sessionId: getPostHogSessionId(client),
  eventId: getActiveEventId(),
  replayUrl: getPostHogReplayUrl(client),
});

export const registerPostHogObservabilityContext = (
  client: PostHogInterface,
  serviceVersion = SERVICE_VERSION,
  deploymentEnvironment = DEPLOYMENT_ENVIRONMENT,
): void => {
  client.register({
    "service.version": serviceVersion,
    "deployment.environment": deploymentEnvironment,
  });
  client.onSessionId((sessionId) => {
    clearActiveEvent();

    const normalizedSessionId = sanitizeSessionId(sessionId);
    setTag(CORRELATION_ATTRIBUTES.sessionId, normalizedSessionId);
    setContext("posthog", {
      session_id: normalizedSessionId,
      replay_url: getPostHogReplayUrl(client),
    });
  });
};

export const activateObservabilityCorrelation = (
  correlation: Partial<RequestCorrelation>,
  client: PostHogInterface = posthog,
): ObservabilityContext | undefined => {
  const sessionId = sanitizeSessionId(correlation.sessionId);
  const eventId = sanitizeEventId(correlation.eventId);

  if (!eventId) return undefined;

  activeEventId = eventId;
  activeEventExpiresAt = Date.now() + ACTIVE_EVENT_CONTEXT_TTL_MS;
  setTag(CORRELATION_ATTRIBUTES.eventId, eventId);
  if (sessionId) {
    setTag(CORRELATION_ATTRIBUTES.sessionId, sessionId);
  }

  const replayUrl = getPostHogReplayUrl(client);
  setContext("posthog", {
    session_id: sessionId,
    replay_url: replayUrl,
  });

  return { sessionId, eventId, replayUrl };
};

export const recordRequestCorrelation = (
  correlation: Partial<RequestCorrelation>,
  client: PostHogInterface = posthog,
): void => {
  const context = activateObservabilityCorrelation(correlation, client);

  if (!context?.eventId) return;

  client.capture("mm_api_request", {
    [CORRELATION_ATTRIBUTES.eventId]: context.eventId,
    ...(context.sessionId ? { [CORRELATION_ATTRIBUTES.sessionId]: context.sessionId } : {}),
  });
};
